import {
  HeartHandshake, ShieldCheck, Sparkles, Users, CheckCircle2,
} from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { CTABand } from "../components/CTABand";

const VALUES = [
  { icon: HeartHandshake, title: "Patience first", text: "We believe kindness and patience matter as much as technical skill." },
  { icon: ShieldCheck, title: "Honesty always", text: "Clear pricing, honest advice, and never selling you something you don't need." },
  { icon: Sparkles, title: "Real understanding", text: "We're not done until you feel confident using your technology yourself." },
  { icon: Users, title: "Like family", text: "We treat every customer the way we'd want our own parents treated." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About Us"
        title="Friendly faces behind The Tech Savvy Company"
        subtitle="We started with a simple belief: technology should be welcoming for everyone — no matter your age or experience."
      />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <img
            src="https://images.unsplash.com/photo-1712159018726-4564d92f3ec2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
            alt="A friendly member of the tech support team"
            className="rounded-[2rem] shadow-2xl w-full h-[400px] lg:h-[500px] object-cover"
          />
        </Reveal>
        <Reveal delay={0.12}>
          <h2 className="font-heading font-extrabold text-foreground text-3xl sm:text-4xl tracking-tight leading-snug">
            Our story
          </h2>
          <div className="mt-5 space-y-4 text-muted-foreground text-lg leading-relaxed">
            <p>
              After watching family members struggle with new phones and confusing
              gadgets, we knew there had to be a kinder way. So we built a company
              around patience, plain language, and genuine care.
            </p>
            <p>
              Based right here in Columbus, Ohio, we visit homes across the area to
              set up devices, solve problems, and teach in a way that actually
              sticks. No talking down, no rushing — just friendly, respectful help.
            </p>
            <p>
              Whether you're setting up your very first smartphone or helping a
              parent stay connected, we're here to make technology feel simple again.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="bg-secondary">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
          <Reveal>
            <h2 className="font-heading font-extrabold text-foreground text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-snug text-center max-w-2xl mx-auto">
              What we stand for
            </h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-border bg-card p-8">
                  <span className="grid place-items-center h-14 w-14 rounded-2xl bg-accent/10 text-accent mb-5">
                    <v.icon className="h-7 w-7" />
                  </span>
                  <h3 className="font-heading font-bold text-xl text-foreground mb-2">{v.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <div className="rounded-[2rem] bg-primary p-10 lg:p-16 grid md:grid-cols-3 gap-10 text-center">
          {[
            ["500+", "Happy customers helped"],
            ["100%", "Plain‑English guarantee"],
            ["Columbus", "Proudly local, Ohio"],
          ].map(([big, small], i) => (
            <Reveal key={small} delay={i * 0.1}>
              <p className="font-heading font-extrabold text-white text-4xl lg:text-5xl">{big}</p>
              <p className="mt-2 text-slate-300 text-lg">{small}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <CTABand />
    </div>
  );
}
